<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PriorityIcon from 'dashboard/routes/dashboard/inbox/components/PriorityIcon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  agentInfo: {
    type: Object,
    default: null,
  },
  loadingAgent: {
    type: Boolean,
    default: false,
  },
  conversationData: {
    type: Object,
    default: null,
  },
  loadingConversation: {
    type: Boolean,
    default: false,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  itemDescription: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['navigate-to-conversation', 'context-menu']);

const { t } = useI18n();

// Recreate computed properties needed locally or pass them as props if complex
const priorityInfo = computed(() => {
  const priority = props.item.item_details?.priority?.toLowerCase() || 'none';
  if (priority === 'none') return null;

  const priorityMap = {
    high: {
      label: t('KANBAN.PRIORITY_LABELS.HIGH'),
      class: 'bg-ruby-50 dark:bg-ruby-800 text-ruby-800 dark:text-ruby-50',
    },
    medium: {
      label: t('KANBAN.PRIORITY_LABELS.MEDIUM'),
      class:
        'bg-yellow-50 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-50',
    },
    low: {
      label: t('KANBAN.PRIORITY_LABELS.LOW'),
      class: 'bg-green-50 dark:bg-green-800 text-green-800 dark:text-green-50',
    },
  };
  return priorityMap[priority] || null;
});

const priorityClass = computed(() => {
  const priorityMap = {
    high: 'bg-ruby-50 dark:bg-ruby-800 text-ruby-800 dark:text-ruby-50',
    medium:
      'bg-yellow-50 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-50',
    low: 'bg-green-50 dark:bg-green-800 text-green-800 dark:text-green-50',
    none: 'bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-50',
  };
  return (
    priorityMap[props.item.item_details?.priority?.toLowerCase() || 'none'] ||
    priorityMap.none
  );
});

const formattedValue = computed(() => {
  if (!props.item.item_details?.value) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(props.item.item_details.value);
});

const truncatedSenderName = computed(() => {
  const name = props.conversationData?.contact?.name;
  const email = props.conversationData?.contact?.email;
  const defaultText = t('KANBAN.ITEM_CONVERSATION.NO_CONTACT');
  const text = name || email || defaultText;

  return text.length > 10 ? `${text.substring(0, 10)}...` : text;
});

const formatStageDate = (item) => {
  if (!item || !item.stage_entered_at) return '';
  try {
    return t('KANBAN.STAGE.TIME_IN_STAGE', {
      time: formatDistanceToNow(new Date(item.stage_entered_at), {
        locale: ptBR,
        addSuffix: false,
      }),
    });
  } catch (error) {
    // console.error('Erro ao calcular tempo na etapa:', error);
    return '';
  }
};

const onNavigateToConversation = (e, conversationDisplayId) => {
  emit('navigate-to-conversation', e, conversationDisplayId);
};

const onContextMenu = (e, conversationId) => {
  emit('context-menu', e, conversationId);
};
</script>

<template>
  <div class="header-section">
    <div class="flex items-center justify-between gap-4 mb-3">
      <h3 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
        {{ props.item.item_details.title }}
      </h3>
      <div class="flex items-center gap-2">
        <PriorityIcon
          :priority="props.item.item_details?.priority || 'none'"
        />
        <span
          v-if="
            props.item.item_details?.priority &&
            props.item.item_details.priority !== 'none'
          "
          class="px-3 py-1.5 text-sm font-medium rounded-full"
          :class="priorityClass"
        >
          {{ priorityInfo?.label || t(`KANBAN.PRIORITY_LABELS.${(props.item.item_details?.priority || 'NONE').toUpperCase()}`) }}
        </span>
        <span
          v-else
          class="px-3 py-1.5 text-sm font-medium rounded-full bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-50"
        >
          {{ t('KANBAN.PRIORITY_LABELS.NONE') }}
        </span>
      </div>
    </div>
    <p
      v-if="itemDescription"
      class="text-base text-slate-600 dark:text-slate-400 mb-4"
    >
      {{ itemDescription }}
    </p>

    <!-- Grid de informações principais -->
    <div class="info-grid">
      <!-- Agente Responsável -->
      <div class="info-card">
        <span class="info-label">
          {{ t('KANBAN.ITEM_DETAILS.AGENT_RESPONSIBLE') }}
        </span>
        <div class="info-content">
          <div v-if="loadingAgent" class="loading-text">
            {{ t('KANBAN.ITEM_DETAILS.LOADING_AGENT') }}
          </div>
          <div v-else-if="agentInfo" class="flex items-center gap-2">
            <Avatar
              :name="agentInfo.name"
              :src="agentInfo.avatar_url"
              :size="28"
            />
            <span class="font-medium">{{ agentInfo.name }}</span>
          </div>
          <div v-else class="no-data-text">
            {{ t('KANBAN.ITEM_DETAILS.NO_AGENT') }}
          </div>
        </div>
      </div>

      <!-- Conversa Relacionada -->
      <div
        v-if="props.item.item_details?.conversation_id"
        class="info-card"
      >
        <span class="info-label">
          {{ t('KANBAN.FORM.CONVERSATION.LABEL') }}
        </span>
        <div
          class="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 min-h-[1.5rem] cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          @click="onNavigateToConversation($event, props.item.item_details?.conversation?.display_id)"
          @contextmenu="onContextMenu($event, props.item.item_details.conversation_id)"
        >
          <div class="flex items-center gap-2 flex-1">
            <fluent-icon
              icon="chat"
              class="text-slate-500 dark:text-slate-400 flex-shrink-0"
              size="16"
            />
            <div
              v-if="conversationData"
              class="flex items-center justify-between w-full"
            >
              <div
                class="text-xs text-slate-700 dark:text-slate-300 truncate my-0"
              >
                #{{ conversationData.display_id }} - {{ truncatedSenderName }}
                <span
                  class="ml-2 text-xs px-2 py-0.5 rounded-full"
                  :class="{
                    'bg-green-100 text-green-700':
                      conversationData.status === 'open',
                    'bg-yellow-100 text-yellow-700':
                      conversationData.status === 'pending',
                    'bg-slate-100 text-slate-700':
                      conversationData.status === 'resolved',
                  }"
                >
                  {{ conversationData.status }}
                </span>
              </div>
              <span
                v-if="conversationData.unread_count > 0"
                class="flex items-center justify-center h-4 min-w-[1rem] px-1 text-[0.625rem] font-medium bg-ruby-500 text-white rounded-full flex-shrink-0"
              >
                {{ conversationData.unread_count > 9 ? '9+' : conversationData.unread_count }}
              </span>
            </div>
            <div
              v-else-if="loadingConversation"
              class="text-xs text-slate-500 flex items-center"
            >
              {{ t('KANBAN.LOADING_CONVERSATION') }}
            </div>
            <div v-else class="text-xs text-slate-500 flex items-center">
              {{ t('KANBAN.CONVERSATION_NOT_FOUND') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Valor -->
      <div v-if="formattedValue" class="info-card">
        <span class="info-label">
          {{ t('KANBAN.FORM.VALUE.LABEL') }}
        </span>
        <span class="info-content value-text">
          {{ formattedValue }}
        </span>
      </div>

      <!-- Estágio Atual -->
      <div class="info-card">
        <span class="info-label">
          {{ t('KANBAN.FORM.STAGE.LABEL') }}
        </span>
        <div class="info-content flex items-center gap-2">
          <span class="font-medium">
            {{ currentStageName }}
          </span>
          <span class="text-xs text-slate-500">
            {{ formatStageDate(item) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-section {
  @apply bg-white dark:bg-slate-800
    rounded-lg
    p-4 mb-4
    border border-slate-100 dark:border-slate-700;
}

.info-grid {
  @apply grid gap-3 mt-3;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.info-card {
  @apply relative overflow-hidden flex flex-col gap-1 p-3 rounded-lg transition-all duration-200
    bg-white dark:bg-slate-800 border dark:border-slate-700;
}

.info-card:nth-child(1) {
  @apply bg-woot-50 dark:bg-slate-800 border-woot-100 dark:border-woot-700/50;
}

.info-card:nth-child(2) {
  @apply bg-green-50 dark:bg-slate-800 border-green-100 dark:border-green-700/50;
}

.info-card:nth-child(3) {
  @apply bg-yellow-50 dark:bg-slate-800 border-yellow-100 dark:border-yellow-700/50;
}

.info-card:nth-child(4) {
  @apply bg-indigo-50 dark:bg-slate-800 border-indigo-100 dark:border-indigo-700/50;
}

.info-label {
  @apply text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400;
}

.info-content {
  @apply text-sm font-medium text-slate-900 dark:text-slate-100;
}

.info-card:hover {
  @apply transform -translate-y-0.5 shadow-sm dark:shadow-slate-900/50;
}

.loading-text,
.no-data-text {
  @apply text-sm text-slate-500;
}

.value-text {
  @apply font-semibold text-green-600 dark:text-green-400;
}
</style> 